import React, { useState, useEffect } from 'react';
import { Amplify, Auth, API, Storage, Cache } from 'aws-amplify';
import { Flex, View, Button, Text, Divider, Radio, SelectField, TextField, Icon, Link } from '@aws-amplify/ui-react';
import { withAuthenticator } from '@aws-amplify/ui-react';
import awsconfig from '../aws-exports';
import GeneralFooter from '../ui-components/GeneralFooter';
import Inventory from '../ui-components/InventoryTable.js';
import { validateSubscription } from '../utils/subscriptionService';
import DialogModal from '../components/dialog-modal.tsx';
import { useNavigate } from 'react-router';

Amplify.configure(awsconfig);

function InventoryContainer() {
    const navigate = useNavigate();
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true); // Loading state for inventory table
    const [subscriptionPlan, setSubscriptionPlan] = useState(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(true);
    const [dialogModalOpen, setDialogModalOpen] = useState(false);
    const [dialogModalMessage, setDialogModalMessage] = useState('');

    const handleCloseModal = () => {
        setDialogModalOpen(false);
    };

    useEffect(() => {
        downloadData();
        validateSubscription(setSubscriptionPlan, setSubscriptionLoading, setDialogModalMessage, setDialogModalOpen, navigate);
    }, []);

    async function downloadData() {
        setLoading(true); // Start loading
        try {
            const market = Cache.getItem('marketCode');

            let mfnURL = market + "_Inventory/MFN/ASIN_latest_data.json";
            let mfnInventoryData = [];
            try {
                const mfnResult = await Storage.vault.get(mfnURL, { download: true });
                mfnInventoryData = await new Response(mfnResult.Body).json();
            } catch (error) {
                console.log("MFN data not found or error occurred:", error);
                mfnInventoryData = [];
            }

            let afnURL = market + "_Inventory/AFN/ASIN_latest_data.json";
            let afnInventoryData = [];
            try {
                const afnResult = await Storage.vault.get(afnURL, { download: true });
                afnInventoryData = await new Response(afnResult.Body).json();
            } catch (error) {
                console.log("AFN data not found or error occurred:", error);
                afnInventoryData = [];
            }

            const mfnData = Object.values(mfnInventoryData).map(item => ({
                asin: item.asin,
                sku: item.sellerSku,
                availableQuantity: item.totalQuantity,
            }));

            const afnData = Object.values(afnInventoryData).map(item => ({
                asin: item.asin,
                sku: item.sellerSku,
                availableQuantity: item.totalQuantity,
            }));

            const combinedDataMap = {};

            [mfnData, afnData].forEach(dataSet => {
                dataSet.forEach(item => {
                    if (combinedDataMap[item.asin]) {
                        combinedDataMap[item.asin].availableQuantity += item.availableQuantity;
                        combinedDataMap[item.asin].sku += `, ${item.sku}`;
                    } else {
                        combinedDataMap[item.asin] = { ...item };
                    }
                });
            });

            const combinedData = Object.values(combinedDataMap).filter(item => item.availableQuantity > 0);
            setTableData(combinedData);
        } catch (exception) {
            console.log(exception);
        } finally {
            setLoading(false); // Stop loading after data is fetched
        }
    }

    return (
        <div style={{ width: "100%" }}>
            <Flex paddingTop={'20px'} paddingLeft={'30px'} paddingRight={'30px'} overflow={'auto'}>
                <Flex gap="32px" direction="column" width="100%" justifyContent="flex-start" alignItems="flex-start" overflow="hidden" position="relative">
                    <Flex gap="8px" direction="column" justifyContent="flex-start" alignItems="flex-start">
                        <Text fontFamily="Inter" fontSize="32px" fontWeight="500" color="rgba(13,26,38,1)" lineHeight="40px" textAlign="left">
                            Inventory
                        </Text>
                    </Flex>
                    {loading ? (
                        <Flex width="100%" height="300px" justifyContent="center" alignItems="center">
                            <Text fontSize="20px" fontWeight="400" color="rgba(13,26,38,1)">
                                Loading inventory data. Please wait...
                            </Text>
                        </Flex>
                    ) : (
                        <Inventory data={tableData} itemsPerPage={10} />
                    )}
                </Flex>
            </Flex>
            <GeneralFooter />
            <DialogModal open={dialogModalOpen} message={dialogModalMessage} onClose={handleCloseModal} />
        </div>
    );
}

export default withAuthenticator(InventoryContainer);
